import EditIcon from '@mui/icons-material/Edit'
import { Alert, Button, Chip, Paper, Snackbar, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid2'
import moment from 'moment'
import React from 'react'
import { useForm, useWatch } from 'react-hook-form'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { i18n } from '../../index/index'
import { AllocationBar } from './AllocationBar'
import { isMonthLocked } from './deriveMonthlyRows'
import { MonthlyTargetsList } from './MonthlyTargetsList'
import { MonthlyTargetsTable } from './MonthlyTargetsTable'
import { ShiftWeightsPanel } from './ShiftWeightsPanel'
import {
  MonthlyActualValue,
  MonthlyTargetsFormData,
  MonthlyTargetsLabels,
  MonthlyTargetValue,
  ShiftPercentages,
} from './types'
import { piecesFormat, ValueFormat, ValueFormatContext } from './valueFormat'
import { YearlyTargetField } from './YearlyTargetField'

export interface MonthlyTargetsEditorProps {
  /** Any date inside the year being edited. */
  date?: Date
  /** Saved yearly target. */
  yearlyTarget?: number | null
  /** Saved monthly targets (feature-neutral shape). */
  monthlyTargets?: MonthlyTargetValue[] | null
  shiftPercentages?: ShiftPercentages | null
  /** Previous year's actuals per month. `undefined` hides the comparison columns. */
  lastYearActuals?: MonthlyActualValue[]
  labels: MonthlyTargetsLabels
  /** Unit formatting (pieces by default, euros for incentive sales). */
  valueFormat?: ValueFormat
  /**
   * First year that uses monthly targets. Boxes for earlier years are "yearly only": just the
   * yearly target and shift weights (no monthly table, no balance rule, no months saved).
   * Used by ilarit for the transition year; omit to always use monthly targets (incentive sales).
   */
  firstMonthlyYear?: number
  /**
   * Whether the user may change the yearly value. Users who can't may only start editing
   * once a yearly target has been saved, but only when `editRequiresYearlyTarget` is set.
   */
  canEditYearlyTarget: boolean
  /**
   * Gate editing on a saved yearly target (next-year box): users who can't set the yearly
   * value see a "not set yet" message until the SOK admin has set it. Off for the current
   * year, which stays editable like before.
   */
  editRequiresYearlyTarget?: boolean
  /** Whether the user may use "divide by budget" (also limited to future years). */
  canUseDivideByBudget: boolean
  /** Loads a budget-weighted split of the given yearly target. */
  fetchTargetsByBudget: (year: number, yearlyTarget: number) => Promise<MonthlyTargetValue[]>
  /** Persists the form. Reject to show the update error. */
  save: (data: MonthlyTargetsFormData) => Promise<unknown>
}

export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const defaultShiftPercentages: ShiftPercentages = { Morning: 33.3, Evening: 33.3, Night: 33.3 }

/**
 * Shared yearly + monthly targets editor (ilarit, incentive sales).
 *
 * Owns the form state, the balance rule (monthly sum must equal the yearly target), the
 * divide-by-budget action and the responsive switch. Everything feature-specific (GraphQL,
 * field names, unit, labels, permissions) is injected through props by a thin wrapper.
 *
 * Layout tiers: stacked list on phones, table (+ shift card below) on tablets, table with a
 * side panel on wide screens.
 */
export function MonthlyTargetsEditor({
  date,
  yearlyTarget: savedYearlyTarget,
  monthlyTargets: savedMonthlyTargets,
  shiftPercentages: savedShiftPercentages,
  lastYearActuals,
  labels,
  valueFormat = piecesFormat,
  firstMonthlyYear,
  canEditYearlyTarget,
  editRequiresYearlyTarget = false,
  canUseDivideByBudget,
  fetchTargetsByBudget,
  save,
}: MonthlyTargetsEditorProps) {
  const theme = useTheme()
  const useListLayout = useMediaQuery(theme.breakpoints.down('md')) // phones → stacked list
  const useSidePanel = useMediaQuery(theme.breakpoints.up('lg')) // wide → shift panel beside
  const [isEditing, setIsEditing] = React.useState(false)
  const [isBusy, setIsBusy] = React.useState(false)
  const [focusedMonth, setFocusedMonth] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  // Shown in the yearly section when "divide by budget" can't produce a split (no budget).
  const [divideError, setDivideError] = React.useState<string | null>(null)

  const savedFormValues = (): MonthlyTargetsFormData => ({
    yearlyTarget: savedYearlyTarget || 0,
    monthlyTargets: savedMonthlyTargets ?? [],
    shiftPercentages: removeTypename<ShiftPercentages>(savedShiftPercentages ?? defaultShiftPercentages),
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<MonthlyTargetsFormData>({
    defaultValues: savedFormValues(),
    mode: 'onChange',
  })

  const watchedYearlyTarget = useWatch({ control, name: 'yearlyTarget' })
  const watchedMonthlyTargets = useWatch({ control, name: 'monthlyTargets' })

  const targetYear = date ? moment(date).year() : moment().year()
  // Transition years (before monthly targets were introduced) only have a yearly target.
  const isYearlyOnly = firstMonthlyYear !== undefined && targetYear < firstMonthlyYear
  // Dividing by budget is only offered to permitted users, and only for future years.
  const canDivideByBudget = !isYearlyOnly && canUseDivideByBudget && (!date || moment(date).year() > moment().year())

  // The next-year box gates editing until the SOK admin has saved a yearly target (> 0).
  // The current-year box (editRequiresYearlyTarget off) is always editable, like before.
  const hasSavedYearlyTarget = (savedYearlyTarget ?? 0) > 0
  const canStartEditing = !editRequiresYearlyTarget || canEditYearlyTarget || hasSavedYearlyTarget

  const toggleEditing = () => {
    setIsEditing(!isEditing)
  }

  const cancelEditing = () => {
    reset(savedFormValues())
    setDivideError(null)
    setIsEditing(false)
  }

  const changeMonthlyTarget = (month: number, value: number) => {
    if (isMonthLocked(targetYear, month)) {
      return
    }
    setDivideError(null)
    const currentTargets = watchedMonthlyTargets || []
    setValue(
      'monthlyTargets',
      currentTargets.map((mt) => (mt.month === month ? { ...mt, value } : mt)),
    )
  }

  // Add the still-unallocated amount (yearly - sum of all 12 months) into the given month,
  // so the total reaches the yearly goal. Used by the "Lisää loput tähän" hint that follows
  // the focused cell when the user has under-allocated (e.g. after reducing a month).
  const fillRemainderIntoMonth = (month: number) => {
    const currentTargets = watchedMonthlyTargets || []
    const sumOfAllMonths = currentTargets.reduce((sum, mt) => sum + mt.value, 0)
    const unallocatedAmount = Math.round((watchedYearlyTarget ?? 0) - sumOfAllMonths)
    if (unallocatedAmount <= 0) {
      return
    }
    const monthValue = currentTargets.find((mt) => mt.month === month)?.value ?? 0
    changeMonthlyTarget(month, monthValue + unallocatedAmount)
  }

  const changeShiftPercentage = (shift: keyof ShiftPercentages, value: number) => {
    setValue(`shiftPercentages.${shift}`, value)
  }

  const changeYearlyTarget = (yearlyTarget: number) => {
    setDivideError(null)
    setValue('yearlyTarget', yearlyTarget)
  }

  const onSubmit = async (data: MonthlyTargetsFormData) => {
    setDivideError(null)
    setIsEditing(false)
    setIsBusy(true)
    try {
      // Yearly-only years never send months, so the backend keeps just the yearly target.
      await save(isYearlyOnly ? { ...data, monthlyTargets: [] } : data)
    } catch {
      setError(labels.updateError)
    } finally {
      setIsBusy(false)
    }
  }

  const onRequestingMonthlyTargets = async () => {
    setDivideError(null)
    setIsBusy(true)
    try {
      const yearlyTarget = watchedYearlyTarget || 0
      const targets = await fetchTargetsByBudget(targetYear, yearlyTarget)
      if (yearlyTarget > 0 && targets.every((target) => !target.value)) {
        // The backend returns all zeros when the station has no budget for the year.
        // Keep the current months and tell the user instead of silently zeroing them.
        setDivideError(i18n.t('admin.monthlyTargets.divideNoBudget', { year: targetYear }))
      } else {
        setValue('monthlyTargets', targets)
      }
    } catch {
      setError(labels.updateError)
    } finally {
      setIsBusy(false)
    }
  }

  const monthlyTargetsSum = (watchedMonthlyTargets || []).reduce((sum, mt) => sum + mt.value, 0)
  let targetDifference = monthlyTargetsSum - (watchedYearlyTarget ?? 0)
  if (Math.abs(targetDifference) < 1) {
    targetDifference = 0
  }
  // Yearly-only years have no months to balance; like before, a yearly target is required.
  const isFormValid = isYearlyOnly ? isValid && (watchedYearlyTarget ?? 0) > 0 : targetDifference === 0 && isValid

  // Amount still to allocate (yearly - sum of all months). Positive = under-allocated, which
  // is when the "Lisää loput tähän" hint should follow the focused cell.
  const unallocated = Math.round((watchedYearlyTarget ?? 0) - monthlyTargetsSum)

  const hasLastYear = !!lastYearActuals
  const lastYearTotal = (lastYearActuals || []).reduce((sum, m) => sum + (m.value ?? 0), 0)
  const lastYearComplete = MONTHS.every((month) => (lastYearActuals || []).some((a) => a.month === month))

  const showMonthlyEditor = !isYearlyOnly

  const monthlyEditor = useListLayout ? (
    <MonthlyTargetsList
      months={MONTHS}
      targetYear={targetYear}
      heading={labels.monthlyTargetsHeading}
      monthlyTargets={watchedMonthlyTargets || []}
      lastYearActuals={lastYearActuals}
      isEditing={isEditing}
      unallocated={unallocated}
      focusedMonth={focusedMonth}
      onFocusMonth={setFocusedMonth}
      changeMonthlyTarget={changeMonthlyTarget}
      fillRemainderIntoMonth={fillRemainderIntoMonth}
    />
  ) : (
    <MonthlyTargetsTable
      months={MONTHS}
      targetYear={targetYear}
      monthlyTargets={watchedMonthlyTargets || []}
      lastYearActuals={lastYearActuals}
      isEditing={isEditing}
      unallocated={unallocated}
      focusedMonth={focusedMonth}
      onFocusMonth={setFocusedMonth}
      changeMonthlyTarget={changeMonthlyTarget}
      fillRemainderIntoMonth={fillRemainderIntoMonth}
    />
  )

  const renderShiftPanel = (variant: 'sidebar' | 'card') => (
    <ShiftWeightsPanel
      shiftPercentages={{
        Morning: watch('shiftPercentages.Morning'),
        Evening: watch('shiftPercentages.Evening'),
        Night: watch('shiftPercentages.Night'),
      }}
      isEditing={isEditing}
      onChange={changeShiftPercentage}
      variant={variant}
    />
  )

  return (
    <ValueFormatContext.Provider value={valueFormat}>
      <Paper
        variant='outlined'
        sx={{
          padding: { xs: 1, sm: 2 },
          borderColor: isEditing ? 'success.main' : 'divider',
          borderWidth: isEditing ? 2 : 1,
          maxWidth: '100%',
        }}
      >
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert onClose={() => setError(null)} severity='error'>
            {error}
          </Alert>
        </Snackbar>
        <Loader loading={isBusy}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='flex-start'
            marginBottom={2}
            flexWrap='wrap'
            gap={1}
          >
            <Typography variant='h6'>{labels.title + ' ' + targetYear}</Typography>
            {isEditing ? (
              <Chip
                label={i18n.t('admin.monthlyTargets.editingChip')}
                size='small'
                color='success'
                variant='outlined'
              />
            ) : canStartEditing ? (
              <Button variant='contained' color='primary' startIcon={<EditIcon />} onClick={toggleEditing}>
                {i18n.t('buttons.edit')}
              </Button>
            ) : (
              <Typography variant='caption' color='text.secondary'>
                {i18n.t('admin.monthlyTargets.yearlyTargetNotSet', { year: targetYear })}
              </Typography>
            )}
          </Stack>
          <YearlyTargetField
            value={watchedYearlyTarget || 0}
            previousValue={savedYearlyTarget || 0}
            isEditing={isEditing}
            canEditYearlyTarget={canEditYearlyTarget}
            canDivideByBudget={canDivideByBudget}
            targetYear={targetYear}
            hasLastYear={hasLastYear}
            lastYearComplete={lastYearComplete}
            lastYearTotal={lastYearTotal}
            divideByBudgetLabel={labels.divideByBudget}
            onChange={changeYearlyTarget}
            divideError={divideError}
            onDivideByBudget={onRequestingMonthlyTargets}
          />
          {!showMonthlyEditor ? (
            // Yearly-only: the shift weights are the only section, across the full width.
            renderShiftPanel('card')
          ) : useSidePanel ? (
            <Grid container>
              <Grid size={{ md: 8 }}>{monthlyEditor}</Grid>
              <Grid size={{ md: 4 }}>{renderShiftPanel('sidebar')}</Grid>
            </Grid>
          ) : (
            <>
              {monthlyEditor}
              {renderShiftPanel('card')}
            </>
          )}
          {isEditing && (
            <AllocationBar
              yearlyTarget={watchedYearlyTarget || 0}
              allocated={monthlyTargetsSum}
              showAllocation={showMonthlyEditor}
              isFormValid={isFormValid}
              onCancel={cancelEditing}
              onSave={handleSubmit(onSubmit)}
            />
          )}
        </Loader>
      </Paper>
    </ValueFormatContext.Provider>
  )
}
